import React, { useState, useEffect, useRef } from 'react';
import { X, UserPlus, Mail, GraduationCap, CheckCircle, AlertCircle, FileText, Upload } from 'lucide-react';
import api from '../api';
import * as XLSX from 'xlsx';

const EnrollmentModal = ({ isOpen, onClose, initialEmail = '', initialDiploma = '', onEnrollSuccess }) => {
    const [emails, setEmails] = useState(initialEmail);
    const [isBulkMode, setIsBulkMode] = useState(false);
    const [results, setResults] = useState(null);
    const [diplomas, setDiplomas] = useState([]);
    const [selectedDiploma, setSelectedDiploma] = useState(initialDiploma);
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [message, setMessage] = useState({ text: '', type: '' });
    const fileInputRef = useRef(null);

    useEffect(() => {
        if (isOpen) {
            setEmails(initialEmail);
            setSelectedDiploma(initialDiploma);
            setResults(null);
            fetchDiplomas();
        }
    }, [isOpen, initialEmail, initialDiploma]);

    const fetchDiplomas = async () => {
        setLoading(true);
        try {
            const res = await api.get('diplomas/');
            setDiplomas(res.data);
            if (res.data.length > 0 && !initialDiploma && !selectedDiploma) {
                setSelectedDiploma(res.data[0].id);
            }
        } catch (err) {
            console.error('Error fetching diplomas:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleFileUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (evt) => {
            try {
                const bstr = evt.target.result;
                const wb = XLSX.read(bstr, { type: 'binary' });
                let allEmails = [];
                const emailRegex = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g;

                wb.SheetNames.forEach((sheetName) => {
                    const ws = wb.Sheets[sheetName];
                    const data = XLSX.utils.sheet_to_json(ws, { header: 1 });
                    data.forEach(row => {
                        row.forEach(cell => {
                            if (cell && typeof cell === 'string') {
                                const matches = cell.match(emailRegex);
                                if (matches) allEmails = [...allEmails, ...matches];
                            }
                        });
                    });
                });

                const uniqueEmails = [...new Set(allEmails)];
                if (uniqueEmails.length > 0) {
                    setEmails(uniqueEmails.join(', '));
                    setIsBulkMode(true);
                    setMessage({ text: `Se extrajeron ${uniqueEmails.length} correos del archivo.`, type: 'success' });
                } else {
                    alert('No se encontraron correos electrónicos válidos en el archivo.');
                }
            } catch (err) {
                console.error('Error parsing file:', err);
                alert('Error al procesar el archivo. Asegúrate de que sea un Excel o CSV válido.');
            }
        };
        reader.readAsBinaryString(file);
        // Reset input
        e.target.value = null;
    };

    const handleEnroll = async (e) => {
        e.preventDefault();
        if (!emails || !selectedDiploma) return;

        setSubmitting(true);
        setMessage({ text: '', type: '' });
        setResults(null);
        try {
            const res = await api.post(`diplomas/${selectedDiploma}/enroll-student/`, { emails });
            setMessage({ text: res.data.status, type: 'success' });
            if (res.data.results) {
                setResults(res.data.results);
            }
            if (onEnrollSuccess) onEnrollSuccess();
            
            // If everything was successful and not many, close after delay
            if (res.data.results?.error.length === 0) {
              setTimeout(() => {
                  if (res.data.results?.error.length === 0) onClose();
              }, 3000);
            }
        } catch (err) {
            const errorMsg = err.response?.data?.error || err.response?.data?.detail || 'Error al realizar la inscripción';
            setMessage({ text: errorMsg, type: 'error' });
        } finally {
            setSubmitting(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="modal-overlay" style={{ zIndex: 1100 }} onClick={onClose}>
            <div className="modal-content glassmorphism animate-scale-in p-4" 
                 style={{ maxWidth: '600px', width: '95%', backgroundColor: '#ffffff', maxHeight: '90vh', overflowY: 'auto' }}
                 onClick={e => e.stopPropagation()}>
                <div className="d-flex justify-content-between align-items-center mb-4 border-bottom border-secondary border-opacity-25 pb-3">
                    <h4 className="mb-0 d-flex align-items-center section-title">
                        <UserPlus className="me-2 text-primary" size={24} /> Inscripción de Alumnos
                    </h4>
                    <button type="button" className="btn-close-premium" onClick={onClose}>
                        <X size={18} />
                    </button>
                </div>

                <form onSubmit={handleEnroll}>
                    <div className="mb-4 text-start">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <label className="form-label small fw-bold text-muted d-flex align-items-center gap-1 mb-0">
                              <Mail size={14} /> {isBulkMode ? 'LISTA DE CORREOS' : 'CORREO(S) DEL ALUMNO'}
                          </label>
                          <div className="d-flex gap-3">
                            <button 
                                type="button" 
                                className="btn btn-link btn-sm p-0 text-success text-decoration-none fw-bold d-flex align-items-center gap-1"
                                onClick={() => fileInputRef.current?.click()}
                            >
                                <Upload size={14} /> Cargar Excel/CSV
                            </button>
                            <button 
                                type="button" 
                                className="btn btn-link btn-sm p-0 text-primary text-decoration-none fw-bold"
                                onClick={() => setIsBulkMode(!isBulkMode)}
                            >
                                {isBulkMode ? 'Modo puntual' : 'Pegar lista'}
                            </button>
                          </div>
                        </div>

                        <input 
                            type="file" 
                            ref={fileInputRef} 
                            style={{ display: 'none' }} 
                            accept=".xlsx, .xls, .csv, .ods" 
                            onChange={handleFileUpload}
                        />
                        
                        {isBulkMode ? (
                          <textarea 
                            className="form-control premium-input text-dark fw-bold" 
                            style={{ backgroundColor: '#f8fafc', border: '2px solid #e2e8f0', minHeight: '120px' }}
                            placeholder="correo1@ejemplo.com, correo2@ejemplo.com&#10;correo3@ejemplo.com..."
                            value={emails}
                            onChange={(e) => setEmails(e.target.value)}
                            required
                          />
                        ) : (
                          <input 
                              type="text" 
                              className="form-control premium-input text-dark fw-bold" 
                              style={{ backgroundColor: '#f8fafc', border: '2px solid #e2e8f0' }}
                              placeholder="alumno@correo.com o varios separados por comas"
                              value={emails}
                              onChange={(e) => setEmails(e.target.value)}
                              required
                          />
                        )}
                        <small className="text-muted d-block mt-1">Puedes separar múltiples correos por comas o saltos de línea, o subir un archivo.</small>
                    </div>

                    <div className="mb-4 text-start">
                        <label className="form-label small fw-bold text-muted d-flex align-items-center gap-1">
                            <GraduationCap size={14} /> SELECCIONAR DIPLOMATURA
                        </label>
                        <select 
                            className="form-select premium-input text-dark fw-bold"
                            style={{ backgroundColor: '#f8fafc', border: '2px solid #e2e8f0' }}
                            value={selectedDiploma}
                            onChange={(e) => setSelectedDiploma(e.target.value)}
                            required
                        >
                            {loading ? (
                                <option>Cargando diplomaturas...</option>
                            ) : diplomas.map(d => (
                                <option key={d.id} value={d.id}>{d.name} ({d.municipio})</option>
                            ))}
                        </select>
                    </div>

                    {message.text && (
                        <div className={`alert ${message.type === 'success' ? 'alert-success' : 'alert-danger'} py-2 px-3 small mb-4 animate-fade-in d-flex align-items-center gap-2`}>
                            {message.type === 'success' ? <CheckCircle size={16} /> : <AlertCircle size={16} />}
                            {message.text}
                        </div>
                    )}

                    {results && results.error.length > 0 && (
                        <div className="mt-3 p-3 bg-danger bg-opacity-10 border border-danger border-opacity-25 rounded mb-4">
                            <h6 className="text-danger fw-bold small mb-2 d-flex align-items-center gap-2">
                                <AlertCircle size={14} /> ALGUNOS CORREOS FALLARON:
                            </h6>
                            <ul className="list-unstyled mb-0 small text-danger overflow-auto" style={{maxHeight: '150px'}}>
                                {results.error.map((err, idx) => (
                                    <li key={idx}><strong>{err.email}</strong>: {err.reason}</li>
                                ))}
                            </ul>
                        </div>
                    )}

                    <div className="d-flex justify-content-end gap-2 mt-4">
                        <button type="button" className="btn btn-secondary" onClick={onClose} disabled={submitting}>
                            Cerrar
                        </button>
                        <button type="submit" className="btn btn-primary-gradient px-4" disabled={submitting || loading}>
                            {submitting ? 'Inscribiendo...' : 'Inscribir Alumnos'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default EnrollmentModal;
